"use strict";
/**
 * Moderation_Retention — the single authority for how long a moderation record is kept.
 */
// Created: 2026-09-14
// Updated: 2026-09-14
//
// ── WHY THIS FILE EXISTS (BL-395) ────────────────────────────────────────────
// The retention period used to be an INLINE EXPRESSION at a dozen writer sites:
// `Math.floor(Date.now() / 1000) + 90 * 24 * 60 * 60`. Because nothing owned the
// number, the tree ended up holding three different retentions for one population
// of rows (90 days, 2 years, and none at all), and THREE TYPE COMMENTS in
// `unified-flagging.ts` declared "90 days from creation" over a writer that stored
// 2 years. A literal is not a convention; it is a coincidence that has held so far.
//
// The concrete harm was worse than the inconsistency. The clock starts at CREATION,
// on a row whose entire purpose is to record that NOBODY HAS ACTED YET, and TTL is
// enabled on the live tables (`infra/cdk/src/constructs/free-pattern-warehouse-table.ts`
// sets `timeToLiveAttribute: 'ttl'`). An unresolved queue item therefore deleted
// itself 90 days after it was created — while the BLOCK on the content lives on the
// content record, not on the moderation row. The outcome was permanently suppressed
// content with nothing left explaining why, and no evidence to appeal against.
//
// ── WHAT WAS DECIDED, AND WHAT WAS NOT ───────────────────────────────────────
// Operator decision: retention becomes TEN YEARS. Storage is negligible at this
// scale and long retention is protective.
//
// The creation-time SHAPE is deliberately KEPT. The alternative — no `ttl` while
// unresolved, retention set at resolve time — was considered and NOT chosen: it
// would restructure every resolve path (39 non-test `REMOVE GSI3PK` sites, none of
// which touches `ttl` today) to fix a harm that ten years already removes. Do not
// "improve" this into the resolve-time shape without the Operator.
//
// Backfill of rows already carrying a short `ttl` is a SEPARATE open question. Those
// rows keep their original schedule unless something rewrites them, and nothing here
// rewrites them.
//
// ── WHAT THIS CONSTANT IS AND IS NOT FOR ─────────────────────────────────────
// It governs MODERATION RECORDS — rows that enter the unified moderation queue. The
// discriminator is the QUEUE KEYS, not the presence of a `ttl`:
//
//     GSI3PK: 'moderation_queue'        (the unified queue)
//     GSI3PK: `unresolved#${status}`    (the older image-moderation shape)
//
// It is NOT for rows that legitimately expire, several of which sit right beside a
// moderation write and are easy to sweep up by accident:
//
//   - `SK: 'force_manual_review'` (resolve-pattern-moderation.ts,
//     resolve-project-moderation.ts) — a resubmission-bypass GRANT, written only when
//     `allow_resubmission === true`. A bypass that LAPSES is a coherent design, so its
//     90-day creation clock is arguably correct.
//   - `field_suggestion_limit#{userId}` — a 24-hour rate-limit row.
//   - The flag idempotency guard in `unified-flagging.ts` — a dedup row, not a queue
//     item, and it keeps its own retention.
//   - `ModerationNoteRecord` (2 years) and the comment `helpful#` tracking row — no
//     queue keys, so not this constant's business.
//
// ── ENFORCEMENT ──────────────────────────────────────────────────────────────
// `scripts/check-moderation-retention.mjs` fails the build if an object literal
// carrying the queue keys assigns a `ttl` that does not come from this constant. It
// rides `npm run test` / `npm run ci` through `packages/lambda-utilities`'s `test`
// script, following the `check-bucket-prefixes.mjs` precedent.
//
// An ABSENT `ttl` is permitted and is not what the guard is for. Some queue writers
// deliberately store no `ttl` at all — `packages/lambda-utilities/src/services/
// operational-incident.ts` calls that absence load-bearing per its Req 8.4 — and a
// guard that demanded presence would be overriding a stated requirement.
Object.defineProperty(exports, "__esModule", { value: true });
exports.MODERATION_RECORD_TTL_SECONDS = void 0;
/**
 * Retention for a moderation record, in SECONDS from creation.
 *
 * Ten years. Add it to an epoch-seconds `now` to get the stored `ttl`:
 *
 * ```ts
 * ttl: Math.floor(Date.now() / 1000) + MODERATION_RECORD_TTL_SECONDS
 * ```
 *
 * 3650 days rather than 10 calendar years: leap days are irrelevant at this
 * magnitude and a fixed day count keeps the value a compile-time constant.
 */
exports.MODERATION_RECORD_TTL_SECONDS = 3650 * 24 * 60 * 60;
